import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

export interface Annotation {
  id: string;
  tool_name: 'signature' | 'initial' | 'text' | 'check' | 'name' | 'email' | 'phone' | 'date' | 'time' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  page_num: number;
  value: string;
  fontSize: string;
  backgroundColor: string;
  text_colour: string;
  rotation: number;
  imageData?: string; // Base64 data URL for image annotations
}

@Injectable({
  providedIn: 'root'
})
export class PdfViewerService {
  private pdfDoc: any = null;
  private pageRendering = false;
  private pageNumPending: number | null = null;

  constructor() { }

  async loadPDF(url: string, httpHeaders?: any): Promise<any> {
    const loadingTask = pdfjsLib.getDocument({
      url: url,
      httpHeaders: httpHeaders,
      withCredentials: true
    });

    this.pdfDoc = await loadingTask.promise;
    return this.pdfDoc;
  }

  async renderPage(
    pageNum: number,
    canvas: HTMLCanvasElement,
    scale: number = 1.5
  ): Promise<void> {
    if (this.pageRendering) {
      this.pageNumPending = pageNum;
      return;
    }

    this.pageRendering = true;

    const page = await this.pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: scale });
    const context = canvas.getContext('2d');

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    const renderTask = page.render(renderContext);
    await renderTask.promise;

    this.pageRendering = false;

    if (this.pageNumPending !== null) {
      const pending = this.pageNumPending;
      this.pageNumPending = null;
      await this.renderPage(pending, canvas, scale);
    }
  }

  getNumPages(): number {
    return this.pdfDoc ? this.pdfDoc.numPages : 0;
  }

  async getPageRotation(pageNum: number): Promise<number> {
    if (!this.pdfDoc) return 0;
    const page = await this.pdfDoc.getPage(pageNum);
    return page.rotate || 0;
  }

  drawAnnotation(
    ctx: CanvasRenderingContext2D,
    annotation: Annotation,
    isSelected: boolean = false
  ): void {
    ctx.save();

    // Apply rotation if needed
    if (annotation.rotation) {
      const centerX = annotation.x + annotation.width / 2;
      const centerY = annotation.y + annotation.height / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate((annotation.rotation * Math.PI) / 180);
      ctx.translate(-centerX, -centerY);
    }

    // Handle image annotations differently
    if (annotation.tool_name === 'image' && annotation.imageData) {
      // Draw image
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, annotation.x, annotation.y, annotation.width, annotation.height);
        // Draw selection border after image loads
        if (isSelected) {
          ctx.strokeStyle = '#0000FF';
          ctx.lineWidth = 3;
          ctx.strokeRect(annotation.x, annotation.y, annotation.width, annotation.height);
        }
      };
      img.src = annotation.imageData;

      // Draw placeholder border while image loads
      if (isSelected) {
        ctx.strokeStyle = '#0000FF';
        ctx.lineWidth = 3;
        ctx.strokeRect(annotation.x, annotation.y, annotation.width, annotation.height);
      }
    } else {
      // Draw background
      ctx.fillStyle = annotation.backgroundColor;
      ctx.fillRect(annotation.x, annotation.y, annotation.width, annotation.height);

      // Draw border
      ctx.strokeStyle = isSelected ? '#0000FF' : annotation.text_colour;
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.strokeRect(annotation.x, annotation.y, annotation.width, annotation.height);

      // Draw text
      ctx.fillStyle = annotation.text_colour;
      ctx.font = `${annotation.fontSize} Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const textX = annotation.x + annotation.width / 2;
      const textY = annotation.y + annotation.height / 2;

      // Wrap text if it's too long
      const maxWidth = annotation.width - 10;
      this.wrapText(ctx, annotation.value, textX, textY, maxWidth, parseInt(annotation.fontSize));
    }

    ctx.restore();
  }

  private wrapText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
  ): void {
    const words = text.split(' ');
    let line = '';
    const lines = [];

    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth && n > 0) {
        lines.push(line);
        line = words[n] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    const startY = y - ((lines.length - 1) * lineHeight) / 2;

    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], x, startY + (i * lineHeight));
    }
  }

  generateAnnotationId(): string {
    return 'annot_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  async exportAnnotationsToPDF(annotations: Annotation[], currentScale: number = 1.5): Promise<string> {
    // Convert annotations from canvas coordinates (at current scale) to PDF points (at scale 1.0)
    const convertedAnnotations = await Promise.all(
      annotations.map(async (annotation) => {
        // Get actual PDF page dimensions at scale 1.0 to store in data
        const page = await this.pdfDoc.getPage(annotation.page_num);
        const viewport = page.getViewport({ scale: 1.0 });

        // Convert coordinates from scaled canvas coordinates to PDF points
        // Canvas coordinates are at currentScale, so divide by scale to get PDF points
        const pdfX = annotation.x / currentScale;
        const pdfY = annotation.y / currentScale;
        const pdfWidth = annotation.width / currentScale;
        const pdfHeight = annotation.height / currentScale;

        // Extract numeric font size from string like "12pt"
        let fontSize = 12;
        if (annotation.fontSize) {
          const fontSizeStr = annotation.fontSize.toString().replace(/[^0-9.]/g, '');
          fontSize = parseFloat(fontSizeStr) || 12;
        }

        return {
          id: annotation.id,
          tool_name: annotation.tool_name,
          x: pdfX.toString(),
          y: pdfY.toString(),
          width: pdfWidth.toString(),
          height: pdfHeight.toString(),
          page_num: annotation.page_num,
          value: annotation.value || '',
          font_size: fontSize.toString(),
          text_colour: annotation.text_colour,
          backgroundColor: annotation.backgroundColor,
          rotation: annotation.rotation || 0,
          can_edit: false,
          data_url: annotation.imageData || ''
        };
      })
    );

    return JSON.stringify(convertedAnnotations);
  }

  async importAnnotationsFromData(data: string): Promise<Annotation[]> {
    try {
      return JSON.parse(data);
    } catch (e) {
      console.error('Error parsing annotation data:', e);
      return [];
    }
  }
}